import { useEffect, useRef, useState } from 'react';
import { useBible } from '../context/BibleContext';
import { useVersesQuery, useChaptersQuery, useAvailableBooksQuery, useCommentCountsQuery } from '../hooks/queries';
import { useBookmarks, useChapterHighlights } from '../hooks/annotations';
import { HIGHLIGHT_MARK_CLASSES, segmentText } from '../services/highlights';
import CommentPanel from './CommentPanel';
import HighlightToolbar from './HighlightToolbar';
import type { Bible, CanonicalBook } from '../types';

interface BiblePanelProps {
  openBibleId: number;
  bibles: Bible[];
  canonicalBooks: CanonicalBook[];
  onRemove: () => void;
  isOnlyPanel: boolean;
}

export default function BiblePanel({ openBibleId, bibles, canonicalBooks, onRemove, isOnlyPanel }: BiblePanelProps) {
  const { openBibles, updateOpenBible } = useBible();
  const [selectedVerseId, setSelectedVerseId] = useState<number | null>(null);
  const [showComments, setShowComments] = useState(false);
  const versesRef = useRef<HTMLDivElement>(null);

  const openBible = openBibles.find(ob => ob.id === openBibleId);

  const { data: verses = [], isLoading: versesLoading, error: versesError } = useVersesQuery(
    openBible?.bibleId ?? 0,
    openBible?.canonicalBookId ?? 0
  );
  const { data: chapters = [] } = useChaptersQuery(
    openBible?.bibleId ?? 0,
    openBible?.canonicalBookId ?? 0
  );
  // Comments hang off the canonical verse, so the counts hold for whichever translation is open.
  const { data: commentCounts = {} } = useCommentCountsQuery(
    openBible?.canonicalBookId ?? 0,
    openBible?.selectedChapter ?? 0
  );
  // Books this translation actually contains; while loading, fall back to all canonical books.
  const { data: availableBooks } = useAvailableBooksQuery(openBible?.bibleId ?? 0);
  const booksForDropdown = availableBooks && availableBooks.length > 0 ? availableBooks : canonicalBooks;
  const selectedBookId = openBible?.canonicalBookId;
  // Highlights are per translation (their offsets index this translation's text); bookmarks are canonical.
  const { highlights, applyHighlight } = useChapterHighlights(
    openBible?.bibleId ?? 0,
    openBible?.canonicalBookId ?? 0,
    openBible?.selectedChapter ?? 0
  );
  const { bookmarks, toggleBookmark } = useBookmarks();
  const targetVerse = openBible?.targetVerse;

  // Opening a bookmark sets targetVerse; scroll to it once its chapter is on screen. While the verses are still
  // loading the element is missing, and this runs again when they arrive.
  useEffect(() => {
    if (!targetVerse) return;
    const el = versesRef.current?.querySelector(`[data-verse-number="${targetVerse}"]`);
    if (!el) return;
    el.scrollIntoView({ block: 'center', behavior: 'smooth' });
    setSelectedVerseId(targetVerse);
    updateOpenBible(openBibleId, { targetVerse: undefined });
  }, [targetVerse, verses, openBible?.selectedChapter, openBibleId, updateOpenBible]);

  // If the selected book has no verses in the current translation (e.g. after switching to a
  // translation that lacks it), move to the first book this translation does contain.
  useEffect(() => {
    if (selectedBookId == null || !availableBooks || availableBooks.length === 0) return;
    if (!availableBooks.some(b => b.id === selectedBookId)) {
      updateOpenBible(openBibleId, { canonicalBookId: availableBooks[0].id, selectedChapter: 1 });
    }
  }, [availableBooks, selectedBookId, openBibleId, updateOpenBible]);

  if (!openBible) {
    return <div className="p-4 text-red-600 dark:text-red-400">Error: Bible data not found</div>;
  }

  const selectClass =
    'w-full pl-2 pr-6 py-1.5 rounded-md text-sm appearance-none transition-colors bg-white border border-gray-300 text-gray-900 hover:border-gray-400 focus:outline-none focus:ring-2 focus:ring-parchment-400 dark:bg-ink-700 dark:border-ink-500 dark:text-parchment-100 dark:hover:border-ink-500';

  const filteredVerses = verses.filter(v => v.chapterNumber === openBible.selectedChapter);

  const handleBibleChange = (bibleId: number) => {
    updateOpenBible(openBibleId, { bibleId, selectedChapter: 1 });
  };

  const handleBookChange = (bookId: number) => {
    updateOpenBible(openBibleId, { canonicalBookId: bookId, selectedChapter: 1 });
  };

  const handleChapterChange = (chapter: number) => {
    updateOpenBible(openBibleId, { selectedChapter: chapter });
  };

  const selectedVerse = selectedVerseId
    ? filteredVerses.find(v => v.verseNumber === selectedVerseId) ?? null
    : null;

  const bookmarkedVerses = new Set(
    bookmarks
      .filter(b => b.canonicalBookId === openBible.canonicalBookId && b.chapterNumber === openBible.selectedChapter)
      .map(b => b.verseNumber)
  );

  const handleVerseClick = (verseNumber: number) => {
    // Dragging across a verse to select text ends in a click on it; that must not open the comments.
    if (!(window.getSelection()?.isCollapsed ?? true)) return;
    setSelectedVerseId(verseNumber);
    setShowComments(true);
  };

  return (
    <div className="flex flex-col h-full min-h-0 rounded-xl overflow-hidden shadow-sm bg-white border border-gray-200 dark:bg-ink-800 dark:border-ink-600">
      {/* Fixed header with selectors — stays put while the verses below scroll */}
      <div className="p-3 flex-shrink-0 border-b bg-gray-50 border-gray-200 dark:bg-ink-700/40 dark:border-ink-600">
        <div className="flex items-start gap-2">
          <div className="grid grid-cols-3 gap-2 flex-1 min-w-0">
            <div className="min-w-0">
              <label className="block text-[10px] font-bold mb-1 uppercase tracking-wider text-gray-500 dark:text-gray-400">Bible</label>
              <select
                value={openBible.bibleId}
                onChange={(e) => handleBibleChange(parseInt(e.target.value))}
                className={selectClass}
              >
                {bibles.map(bible => (
                  <option key={bible.id} value={bible.id}>{bible.name}</option>
                ))}
              </select>
            </div>

            <div className="min-w-0">
              <label className="block text-[10px] font-bold mb-1 uppercase tracking-wider text-gray-500 dark:text-gray-400">Book</label>
              <select
                value={openBible.canonicalBookId}
                onChange={(e) => handleBookChange(parseInt(e.target.value))}
                className={selectClass}
              >
                {booksForDropdown.map(book => (
                  <option key={book.id} value={book.id}>{book.name}</option>
                ))}
              </select>
            </div>

            <div className="min-w-0">
              <label className="block text-[10px] font-bold mb-1 uppercase tracking-wider text-gray-500 dark:text-gray-400">Chapter</label>
              <select
                value={openBible.selectedChapter}
                onChange={(e) => handleChapterChange(parseInt(e.target.value))}
                className={selectClass}
              >
                {chapters.length === 0 ? (
                  <option>Loading...</option>
                ) : (
                  chapters.map(chapter => (
                    <option key={chapter} value={chapter}>Chapter {chapter}</option>
                  ))
                )}
              </select>
            </div>
          </div>

          {/* Close panel. Hidden on the last remaining panel, which must stay open. */}
          {!isOnlyPanel && (
            <button
              onClick={onRemove}
              title="Close panel"
              aria-label="Close panel"
              className="mt-0.5 p-1 rounded transition-colors text-gray-400 hover:text-gray-700 hover:bg-gray-200 dark:text-gray-500 dark:hover:text-parchment-100 dark:hover:bg-ink-600"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
      </div>

      {/* Verses + Comment Panel side by side */}
      <div className="flex-1 min-h-0 flex flex-row overflow-hidden">
        <div ref={versesRef} className="flex-1 min-w-0 overflow-y-auto p-4">
          <HighlightToolbar containerRef={versesRef} onApply={applyHighlight} />
          {versesLoading ? (
            <div className="text-gray-500 dark:text-gray-400 text-sm">Loading verses...</div>
          ) : versesError ? (
            <div className="text-red-600 dark:text-red-400 text-sm">Error: {(versesError as Error).message}</div>
          ) : chapters.length === 0 ? (
            <div className="text-gray-500 dark:text-gray-400 text-sm">No chapters available</div>
          ) : filteredVerses.length === 0 ? (
            <div className="text-gray-500 dark:text-gray-400 text-sm">No verses found</div>
          ) : (
            <div className="space-y-1">
              {filteredVerses.map(verse => {
                // A verse that already carries comments keeps its icon on screen; the rest only offer
                // the affordance on hover, so the chapter still reads as plain text.
                const commentCount = commentCounts[verse.verseNumber] ?? 0;
                const hasComments = commentCount > 0;
                const isBookmarked = bookmarkedVerses.has(verse.verseNumber);
                return (
                  <div
                    key={verse.verseNumber}
                    onClick={() => handleVerseClick(verse.verseNumber)}
                    className={`p-2 rounded-lg transition-colors cursor-pointer flex items-start justify-between group border ${
                      selectedVerseId === verse.verseNumber
                        ? 'bg-parchment-100 border-parchment-300 dark:bg-ink-700 dark:border-ink-500'
                        : 'border-transparent hover:bg-gray-50 dark:hover:bg-ink-700/60'
                    }`}
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex gap-2 items-baseline">
                        <span className="font-bold text-sm min-w-fit flex-shrink-0 text-parchment-500 dark:text-parchment-300">
                          {verse.verseNumber}
                        </span>
                        {/* The text content of this element must be exactly verseText: the highlight toolbar reads
                            selection offsets from it. */}
                        <p
                          data-verse-number={verse.verseNumber}
                          className="text-[15px] leading-relaxed text-gray-700 dark:text-gray-300"
                        >
                          {segmentText(verse.verseText, highlights[verse.verseNumber]).map((segment, i) =>
                            segment.color ? (
                              <span key={i} className={`rounded-sm ${HIGHLIGHT_MARK_CLASSES[segment.color]}`}>
                                {segment.text}
                              </span>
                            ) : (
                              <span key={i}>{segment.text}</span>
                            )
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="ml-3 flex-shrink-0 flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        toggleBookmark({
                          canonicalBookId: openBible.canonicalBookId,
                          chapterNumber: openBible.selectedChapter,
                          verseNumber: verse.verseNumber,
                        });
                      }}
                      className={`transition-colors ${
                        isBookmarked
                          ? 'opacity-100 text-parchment-500 hover:text-parchment-600 dark:text-parchment-300 dark:hover:text-parchment-200'
                          : 'opacity-100 md:opacity-0 md:group-hover:opacity-100 text-parchment-400 hover:text-parchment-500 dark:text-parchment-300 dark:hover:text-parchment-200'
                      }`}
                      title={isBookmarked ? 'Remove bookmark' : 'Bookmark this verse'}
                      aria-label={isBookmarked ? `Remove bookmark on verse ${verse.verseNumber}` : `Bookmark verse ${verse.verseNumber}`}
                      aria-pressed={isBookmarked}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 24 24"
                        fill={isBookmarked ? 'currentColor' : 'none'}
                        stroke="currentColor"
                        strokeWidth={1.8}
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                      </svg>
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedVerseId(verse.verseNumber);
                        setShowComments(true);
                      }}
                      className={`flex items-center gap-1 transition-colors ${
                        hasComments
                          ? 'opacity-100 text-parchment-500 hover:text-parchment-600 dark:text-parchment-300 dark:hover:text-parchment-200'
                          : 'opacity-100 md:opacity-0 md:group-hover:opacity-100 text-parchment-400 hover:text-parchment-500 dark:text-parchment-300 dark:hover:text-parchment-200'
                      }`}
                      title={hasComments ? `${commentCount} comment${commentCount > 1 ? 's' : ''}` : 'View/Add comments'}
                      aria-label={
                        hasComments
                          ? `Verse ${verse.verseNumber}, ${commentCount} comment${commentCount > 1 ? 's' : ''}`
                          : `Add a comment on verse ${verse.verseNumber}`
                      }
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        viewBox="0 0 24 24"
                        fill={hasComments ? 'currentColor' : 'none'}
                        stroke="currentColor"
                        strokeWidth={1.8}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"
                        />
                      </svg>
                      {hasComments && <span className="text-xs font-semibold tabular-nums">{commentCount}</span>}
                    </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {showComments && selectedVerse && (
          <div className="fixed inset-0 z-50 overflow-y-auto bg-white dark:bg-ink-800 md:static md:inset-auto md:z-auto md:border-l md:border-gray-200 md:dark:border-ink-600 md:w-80 md:flex-shrink-0">
            <CommentPanel
              selectedVerse={{
                ...selectedVerse,
                reference: `${canonicalBooks.find(b => b.id === openBible.canonicalBookId)?.name} ${openBible.selectedChapter}:${selectedVerse.verseNumber}`,
              }}
              canonicalBookId={openBible.canonicalBookId}
              chapterNumber={openBible.selectedChapter}
              onClose={() => {
                setShowComments(false);
                setSelectedVerseId(null);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
}
